// Web (Next.js) wiring for the shared auth store: injects Sentry user
// context, PostHog identity, auth-cookie sync and the server-action-backed
// flows. Import order matters — './pocketbase' configures the shared package
// before '@racefriends/shared/auth' is evaluated.
import { pb } from './pocketbase';
import { Pilot } from './types';
import { RecordModel } from 'pocketbase';
import * as Sentry from '@sentry/nextjs';
import {
  useAuthStore,
  initializeAuthStore,
  setAuthEffects,
  mapRecordToPilot,
  type AuthSnapshot,
} from '@racefriends/shared/auth';
import { identifyPosthogUser, resetPosthogUser } from './posthog-client';

const linkPilot = async ({
  user,
  email,
  cpf,
}: {
  user: RecordModel;
  email: string;
  cpf?: string;
}): Promise<Pilot | null> => {
  const response = await fetch('/api/pilots/link', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      email,
      cpf,
      userId: user.id,
      userName: user.name,
    }),
  });

  if (response.ok) {
    const data = await response.json();
    const pilot = data.pilot;
    user.piloto = pilot.id;
    return pilot;
  }

  // Create a new pilot if linking failed (no existing pilot found)
  try {
    // Before creating, block if CPF already exists for another account
    if (cpf) {
      try {
        const existingByCpf = await pb
          .collection('Pilotos')
          .getFirstListItem(`cpf = "${cpf}"`, { fields: 'email' });
        if (existingByCpf) {
          throw new Error(
            'cpf_conflict: Já existe uma conta para este CPF. Entre em contato com o suporte informando seu CPF para recuperar seu e-mail.',
          );
        }
      } catch (err: unknown) {
        const status =
          typeof err === 'object' && err !== null && 'status' in err
            ? (err as { status?: number }).status
            : undefined;
        if (status !== 404) throw err;
      }
    }

    const pilotData = {
      nome: user.name,
      nome_completo: user.name,
      email,
      cpf: cpf || '',
      cnh: '',
      categoria: '',
      dataNascimento: '',
      phone: '',
      tipo: 'usuario',
    };

    const newPilotRecord = await pb.collection('Pilotos').create(pilotData);

    // Link the new pilot to the user
    await pb.collection('users').update(user.id, {
      piloto: newPilotRecord.id,
    });

    // Update the user object with the piloto reference
    user.piloto = newPilotRecord.id;

    return mapRecordToPilot(newPilotRecord);
  } catch (error) {
    console.error('Error creating new pilot:', error);
    return null;
  }
};

// Helper function to set Sentry user context
const setSentryUserContext = ({
  isAuthenticated,
  user,
  pilot,
}: AuthSnapshot) => {
  if (typeof window !== 'undefined') {
    // Only run on client side
    if (isAuthenticated && user) {
      const sentryUserData = {
        id: user.id,
        email: user.email,
        username: pilot?.nome || user.email,
        ...(pilot && {
          pilot_id: pilot.id,
          pilot_name: pilot.nome,
          pilot_category: pilot.categoria,
        }),
      };

      // Set user in global scope
      Sentry.setUser(sentryUserData);

      // Also set user in the current scope for persistence
      const currentScope = Sentry.getCurrentScope();
      currentScope.setUser(sentryUserData);

      // Store user data globally for beforeSend hook
      (
        window as unknown as { __SENTRY_USER_DATA__?: unknown }
      ).__SENTRY_USER_DATA__ = sentryUserData as unknown;

      // Add additional context
      Sentry.setContext('user_details', {
        user_id: user.id,
        user_email: user.email,
        user_name: pilot?.nome || user.email,
        user_verified: user.verified,
        user_created: user.created,
        ...(pilot && {
          pilot_id: pilot.id,
          pilot_name: pilot.nome,
          pilot_category: pilot.categoria,
          pilot_cpf: pilot.cpf ? '***' : null,
          pilot_phone: pilot.phone ? '***' : null,
          pilot_instagram: pilot.instagram,
        }),
      });
    } else {
      Sentry.setUser(null);
      const currentScope = Sentry.getCurrentScope();
      currentScope.setUser(null);
      Sentry.setContext('user_details', null);
      delete (window as unknown as { __SENTRY_USER_DATA__?: unknown })
        .__SENTRY_USER_DATA__;
    }
  }
};

setAuthEffects({
  linkPilot,
  registerUser: async (name, email, cpf, password, passwordConfirm) => {
    const { registerUserAction } = await import('@/app/actions/auth');
    const result = await registerUserAction(
      name,
      email,
      cpf,
      password,
      passwordConfirm,
    );
    if (!result.success) {
      throw new Error(result.error);
    }
  },
  afterLogin: (userId) => {
    // Update IP tracking (fire and forget, don't block login)
    import('@/app/actions/update-ip-tracking').then(
      ({ updateUserIpTrackingAction }) => {
        updateUserIpTrackingAction(userId).catch((error) => {
          console.error('Error updating IP tracking:', error);
        });
      },
    );
  },
  syncSession: () => {
    // Keep middleware/server cookie in sync with pb.authStore
    if (typeof document !== 'undefined') {
      document.cookie = pb.authStore.exportToCookie({
        httpOnly: false,
        path: '/',
      });
      if (!pb.authStore.isValid) {
        // Also clear any fallback jwtToken if it exists
        document.cookie = 'jwtToken=; Max-Age=0; Path=/;';
      }
    }
  },
  onAuthChanged: (snapshot) => {
    setSentryUserContext(snapshot);
    if (snapshot.isAuthenticated && snapshot.user) {
      identifyPosthogUser(snapshot.user, snapshot.pilot);
    } else {
      resetPosthogUser();
    }
  },
});

export { useAuthStore, initializeAuthStore };
